import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  fromEvent,
  map,
  merge,
  of,
  shareReplay
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ReadyBoardDataService } from './ready-board-data.service';
import { ReadyBoardConnectionStatus } from './ready-board.models';

export type ReadyBoardConnectionTone = 'ok' | 'degraded' | 'demo';

export interface ReadyBoardConnectionIndicator {
  label: string;
  tone: ReadyBoardConnectionTone;
}

@Injectable({ providedIn: 'root' })
export class ReadyBoardConnectionService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly readyBoardData = inject(ReadyBoardDataService);

  private readonly firestoreEnabled = hasFirebaseConfig(environment);

  private readonly online$ = createOnlineStream(this.platformId).pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly connectionStatus$: Observable<ReadyBoardConnectionStatus> = combineLatest([
    this.online$,
    this.readyBoardData.source$
  ]).pipe(
    map(([online, source]) => (online && source === 'firestore') ? 'ok' : 'degraded'),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly indicator$: Observable<ReadyBoardConnectionIndicator> = this.connectionStatus$.pipe(
    map(status => {
      if (!this.firestoreEnabled) {
        return { label: 'DEMO', tone: 'demo' } as ReadyBoardConnectionIndicator;
      }
      if (status === 'ok') {
        return { label: 'Conexiune stabilă', tone: 'ok' } as ReadyBoardConnectionIndicator;
      }
      return { label: 'Conexiune instabilă', tone: 'degraded' } as ReadyBoardConnectionIndicator;
    }),
    distinctUntilChanged((a, b) => a.label === b.label && a.tone === b.tone),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}

function hasFirebaseConfig(config: typeof environment): boolean {
  const firebase = config?.firebase;
  return Boolean(firebase && firebase.apiKey && firebase.projectId);
}

function createOnlineStream(platformId: object): Observable<boolean> {
  if (!isPlatformBrowser(platformId)) return of(true);

  const online$ = fromEvent(window, 'online').pipe(map(() => true));
  const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

  return merge(of(navigator.onLine), online$, offline$).pipe(distinctUntilChanged());
}
